import * as z from 'zod';
import {
  deliveryMethodSchema,
  deliveryStageSchema,
  geoPositionSchema,
  listResponseMetaSchema,
  merchantOrderStatusSchema,
  optional,
  orderStatusSchema,
  paymentTypeSchema,
  timestampSchema,
  uint64,
  weekdaySchema,
} from './common';

// Order Service — `v1Order` with its line items, wrapped for the merchant in
// `v1RestaurantOrder` (the order plus the merchant's decision deadline), and
// the weekday-count histogram. Money fields are integer so'm.

/**
 * One option a customer picked inside a modifier group (`v1SelectedModifierOption`).
 *
 * `amount` is how many of that option were taken — the API's own name for the
 * per-option quantity, distinct from the LINE's `quantity`. It is not a price:
 * `v1SelectedModifierOption` carries no money at all, so an option's cost is
 * already folded into the line's `price`.
 */
export const selectedModifierOptionSchema = z.object({
  option_id: z.string().default(''),
  option_name: z.string().default(''),
  amount: z.number().int().nonnegative().default(0),
});
export type SelectedModifierOption = z.infer<typeof selectedModifierOptionSchema>;

/**
 * One resolved modifier group on a line item (`v1SelectedModifier`).
 *
 * Display-ready: order-service resolves the group and option NAMES from the
 * catalogue when the order is placed, so the panel never has to join back to
 * the menu to render what the customer chose.
 */
export const selectedModifierSchema = z.object({
  group_id: z.string().default(''),
  group_name: z.string().default(''),
  options: z.array(selectedModifierOptionSchema).default([]),
});
export type SelectedModifier = z.infer<typeof selectedModifierSchema>;

export const orderItemSchema = z.object({
  product_id: z.string().default(''),
  name: z.string().default(''),
  price: z.number().int().nonnegative().default(0),
  quantity: z.number().int().nonnegative().default(0),
  image: z.string().default(''),
  external_id: z.string().default(''),
  /**
   * The id of THIS line (distinct from `product_id`, which the same product
   * shares across orders), plus the modifiers chosen for it.
   *
   * `selected_modifiers` is `v1SelectedModifier[]` — the resolved, display-ready
   * shape the spec now documents. An order placed without modifiers sends it
   * empty, which is why every member still defaults.
   */
  item_id: z.string().default(''),
  selected_modifiers: z.array(selectedModifierSchema).default([]),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

/**
 * `v1CourierInfo` (v1.00.227) — who is carrying the order. The backend sends it
 * EMPTY today ("сейчас всегда пустой" per the release notes), so every member
 * defaults; a non-empty `name`/`phone` is the signal a courier was assigned.
 */
export const courierInfoSchema = z.object({
  name: z.string().default(''),
  phone: z.string().default(''),
  /** The delivery provider's live-tracking map URL, when they expose one. */
  tracking_url: z.string().default(''),
});
export type CourierInfo = z.infer<typeof courierInfoSchema>;

export const orderSchema = z.object({
  id: z.string(),
  /**
   * Monotonic change number. The realtime events carry the same number at
   * their top level (`version`), and the client drops any event/REST card at
   * or below the last applied one — see `model/realtime.ts`.
   */
  version: z.number().int().nonnegative().default(0),
  user_id: z.string().default(''),
  restaurant_id: z.string().default(''),
  status: optional(orderStatusSchema),
  merchant_order_status: optional(merchantOrderStatusSchema),
  total_price: z.number().int().nonnegative().default(0),
  delivery_fee: z.number().int().nonnegative().default(0),
  service_fee: z.number().int().nonnegative().default(0),
  delivery_geo: optional(geoPositionSchema),
  delivery_address: z.string().default(''),
  leave_at_door: z.boolean().default(false),
  courier_comment: z.string().default(''),
  payment_type: optional(paymentTypeSchema),
  phone_number: z.string().default(''),
  scheduled_at: optional(timestampSchema),
  created_at: optional(timestampSchema),
  updated_at: optional(timestampSchema),
  items: z.array(orderItemSchema).default([]),
  dispatch_status: z.string().default(''),
  last_payment_error: z.string().default(''),
  delivery_method: optional(deliveryMethodSchema),
  /**
   * Human-facing number (distinct from the opaque `id`). Older backends send a
   * bare integer; newer ones a formatted string such as "02-100063".
   */
  order_number: z.union([z.number().int().nonnegative(), z.string()]).default(0),
  /** Noor courier service order id; 0 until the order is registered with Noor. */
  noor_order_id: z.number().int().nonnegative().optional(),
  delivery_provider: z.string().default(''),
  /** Lifecycle stamps, set as the order advances. */
  merchant_accepted_at: optional(timestampSchema),
  ready_at: optional(timestampSchema),
  picked_up_by_courier_at: optional(timestampSchema),
  /** Address detail lines. */
  entrance: z.string().default(''),
  floor: z.string().default(''),
  apartment: z.string().default(''),
  // Courier-tracking surface, new in v1.00.227. Primarily for the customer's
  // order screen, but carried on `v1Order` itself so the merchant reads parse
  // them too (protobuf-JSON omits each at its zero value, hence the defaults).
  delivery_stage: deliveryStageSchema.default('DELIVERY_STAGE_UNSPECIFIED'),
  /** Delivery ETA window in minutes; 0 = unknown. */
  eta_min_minutes: z.number().int().nonnegative().default(0),
  eta_max_minutes: z.number().int().nonnegative().default(0),
  /** Why the order was cancelled (customer cancellations); empty otherwise. */
  cancel_reason: z.string().default(''),
  /** Empty object until a courier is assigned — see `courierInfoSchema`. */
  courier: optional(courierInfoSchema),
});
export type Order = z.infer<typeof orderSchema>;

/**
 * `v1RestaurantOrder` — the merchant-facing view of an order. The deadline is
 * set only while the accept/reject decision is still pending.
 *
 * The pickup pair is new in v1.00.227 and sits HERE, beside the deadline, not
 * inside `order`: `courier_pickup_at` is when the courier will arrive at the
 * restaurant (noor-delivery orders, set once paid), and `can_adjust_pickup_time`
 * is true while the restaurant may still move it ±5 minutes (paid, decision not
 * yet made, provider noor) — see `orderApi.adjustCourierPickupTime`.
 */
export const restaurantOrderSchema = z.object({
  order: orderSchema,
  merchant_order_status: optional(merchantOrderStatusSchema),
  merchant_decision_deadline: optional(timestampSchema),
  courier_pickup_at: optional(timestampSchema),
  can_adjust_pickup_time: z.boolean().default(false),
  /**
   * What the delivery cost IN FULL, before the brand's agreement split it
   * between the customer and the merchant. `order.delivery_fee` is only the
   * customer's part of it.
   */
  delivery_cost: uint64,
  /**
   * The merchant's part of `delivery_cost`: not charged to the customer, but
   * withheld from the brand's payout. `order.delivery_fee + this = delivery_cost`.
   */
  merchant_delivery_share: uint64,
});
export type RestaurantOrder = z.infer<typeof restaurantOrderSchema>;

/** Single-resource payload — `data` on get / respond / preparation-status. */
export const restaurantOrderResponseSchema = z.object({
  order: restaurantOrderSchema,
});

/** `v1WeekdayOrderCount` — one bar of the orders-per-weekday histogram. */
export const weekdayOrderCountSchema = z.object({
  weekday: weekdaySchema,
  count: z.number().int().nonnegative().default(0),
});
export type WeekdayOrderCount = z.infer<typeof weekdayOrderCountSchema>;

/** Counts returned in the `data` block of `GET /orders/by-restaurant`. */
export const orderCountsSchema = z.object({
  all: z.number().int().nonnegative().default(0),
  new: z.number().int().nonnegative().default(0),
  preparing: z.number().int().nonnegative().default(0),
  completed: z.number().int().nonnegative().default(0),
});
export type OrderCounts = z.infer<typeof orderCountsSchema>;

/** Envelope schema for `GET /orders/by-restaurant` with `counts` in `data`. */
export const orderListResponseSchema = z.object({
  list: z.array(restaurantOrderSchema).default([]),
  meta: listResponseMetaSchema.default({ total: 0 }),
  data: z
    .object({
      counts: orderCountsSchema.default({
        all: 0,
        new: 0,
        preparing: 0,
        completed: 0,
      }),
    })
    .default({ counts: { all: 0, new: 0, preparing: 0, completed: 0 } }),
});
export type OrderListResult = z.infer<typeof orderListResponseSchema>;
